// ─────────────────────────────────────────────────────────────────────────────
// Correlación READ-ONLY de los salientes de TurnosIA:
//   evento  ↔  texto interno (in-window, editable en Ajustes)  ↔  plantilla Meta
//   (fuera de la ventana de 24h)  ↔  orden de variables.
//
// Es un ESPEJO de las convenciones system-owned del backend (NO cambia el mapeo):
//   • turnosIA  services/WaiaTemplateFallback.ts  → TEMPLATE_NAMES (kind → nombre Meta)
//   • turnosIA  services/TemplateService.ts        → DEFAULT_TEMPLATES (texto interno)
//   • turnosIA  jobs/ReminderJob.ts                → split reminder ≤12h "hoy" / >12h "futuro"
//   • WAIA      services/TurnosStandardTemplates.ts→ STANDARD_TEMPLATES (set + varOrder)
//
// Solo visibilidad. Verificado dist==fuente en el deploy vigente (co15).
// ─────────────────────────────────────────────────────────────────────────────

// Etiquetas legibles de cada texto interno (MessageTemplate.templateKey de turnosIA).
export const TURNOS_INTERNAL_LABELS = {
  booking_confirmed: "Turno agendado",
  appointment_rescheduled: "Turno reprogramado",
  reminder: "Recordatorio",
  confirmation_request: "Solicitud de confirmación",
  llamar_siguiente: "Es tu turno (pase)",
  rebalance_offer: "Oferta de rebalanceo",
  waitinglist_offer: "Oferta de lista de espera",
  cancellation_confirmed: "Cancelación confirmada",
  waitinglist_added: "Alta en lista de espera",
  arrival_check: "¿Estás llegando? (consulta de llegada)",
};

// Texto interno PREDETERMINADO (espejo de turnosIA TemplateService.DEFAULT_TEMPLATES).
// Se usa como preview en el panel superadmin, que no lee los textos editados de cada
// empresa (el admin ve el texto real en Ajustes). Referencia, no fuente de envío.
export const TURNOS_DEFAULT_INTERNAL_TEXT = {
  booking_confirmed:
    "{nombre}\nTu turno quedó confirmado. ✅\n📆 Día: {fecha}\n⏰ Hora: {hora}\n👥 Profesional: {profesional}\n💼 Servicio: {servicio}\n🏥 Sede: {sede1}\n¡Te esperamos en {empresa}!",
  appointment_rescheduled:
    "{nombre}\nCambiamos tu turno de {servicio}. 🔄\n📆 Nuevo día: {fecha}\n⏰ Nueva hora: {hora}\n👥 Profesional: {profesional}\nSi este horario no te sirve, respondé este mensaje. — {empresa}",
  reminder:
    "{nombre}\nRecordamos tu turno en {empresa}. ✅\n📆 Día: {fecha}\n⏰ Hora: {hora}\n👥 Profesional: {profesional}\n💼 Servicio: {servicio}\n🏥 Sede: {sede1}",
  confirmation_request:
    "{nombre}\nSe acerca tu turno en {empresa}.\n📆 Día: {fecha}\n⏰ Hora: {hora}\n👥 Profesional: {profesional}\n¿Podés asistir? Respondé *SI* o *NO*.",
  rebalance_offer:
    "{nombre}\nSe liberó un turno en {empresa}.\n📆 Día: {fecha}\n⏰ Hora: {hora}\n👥 Profesional: {profesional}\n💼 Servicio: {servicio}\n¿Te interesa tomarlo? Tenés {minutos_respuesta} minutos para responder.",
  waitinglist_offer:
    "{nombre}\nHay un turno disponible en {empresa}.\n📆 Día: {fecha}\n⏰ Hora: {hora}\n👥 Profesional: {profesional}\n💼 Servicio: {servicio}\n¿Lo tomás? Tenés {minutos_respuesta} minutos para responder.",
  cancellation_confirmed:
    "{nombre}\nTu turno en {empresa} fue cancelado.\n📆 Día: {fecha}\n⏰ Hora: {hora}\n👥 Profesional: {profesional}\n¿Querés quedar en lista de espera?",
  waitinglist_added:
    "{nombre}\nQuedaste en lista de espera en {empresa}.\n💼 Servicio: {servicio}\n👥 Profesional: {profesional}\nTe avisamos si se libera un turno. ✅",
  llamar_siguiente:
    "{nombre}\nEs tu turno con {profesional}.\nPor favor dirigite al consultorio. 🏥",
  arrival_check:
    "{nombre}, te esperábamos a las {hora} en {empresa}. ¿Estás llegando? Si ya estás acá, respondé *YA ESTOY* y te anunciamos.",
};

// ── Correlación por CLAVE INTERNA (vista admin: parte del texto interno editable) ──
// meta: lista de plantillas Meta que ese texto interno respalda fuera de 24h.
//       vacío = "solo texto libre" (respuesta dentro de la conversación, sin Meta).
export const TURNOS_CORRELATION_BY_INTERNAL = {
  booking_confirmed: {
    event: "Confirmación de reserva",
    trigger: "Al reservar el turno",
    editable: true,
    meta: [{ name: "turno_agendado", varOrder: ["nombre", "servicio", "fecha", "hora"] }],
  },
  appointment_rescheduled: {
    event: "Turno reprogramado",
    trigger: "Al mover un turno con aviso (drag&drop o botón Reprogramar)",
    editable: true,
    meta: [{ name: "turno_reprogramado", varOrder: ["nombre", "servicio", "fecha", "hora"] }],
  },
  reminder: {
    event: "Recordatorio del turno",
    trigger: "Secuencia de recordatorios (o recordatorio manual)",
    editable: true,
    // 1:many — el MISMO texto interno respalda dos plantillas Meta según la anticipación.
    meta: [
      { name: "recordatorio_turno", varOrder: ["nombre", "servicio", "fecha", "hora"], when: "Con anticipación (más de 12 h antes)" },
      { name: "recordatorio_hoy", varOrder: ["nombre", "hora", "servicio"], when: "El día del turno (12 h antes o menos)" },
    ],
  },
  confirmation_request: {
    event: "Pedido de confirmación",
    trigger: "Paso de confirmación (~48 h antes)",
    editable: true,
    meta: [{ name: "confirmacion_asistencia", varOrder: ["nombre", "servicio", "fecha", "hora"] }],
  },
  llamar_siguiente: {
    event: "Pase / es tu turno",
    trigger: "Al llamar al siguiente (sala de espera)",
    editable: false, // texto de sistema; no aparece en el editor de Ajustes
    meta: [{ name: "es_tu_turno", varOrder: ["nombre", "servicio"] }],
  },
  rebalance_offer: {
    event: "Oferta de adelanto (rebalanceo)",
    trigger: "Se libera un turno y se ofrece a un candidato",
    editable: true,
    meta: [], // hoy sale como texto libre por el bot (turno_liberado existe pero sin envío automático)
    relatedMeta: "turno_liberado",
  },
  waitinglist_offer: {
    event: "Oferta de lista de espera",
    trigger: "Se libera un turno y se ofrece a la lista de espera",
    editable: true,
    meta: [],
    relatedMeta: "turno_liberado",
  },
  cancellation_confirmed: {
    event: "Cancelación confirmada",
    trigger: "Respuesta al cancelar (dentro de la conversación)",
    editable: true,
    meta: [],
  },
  waitinglist_added: {
    event: "Alta en lista de espera",
    trigger: "Respuesta al anotarse en lista de espera",
    editable: true,
    meta: [],
  },
  arrival_check: {
    event: "Consulta de llegada",
    trigger: "N min después del inicio de un turno confirmado sin «Llegó» (si la empresa lo activó)",
    editable: true,
    meta: [{ name: "consulta_llegada", varOrder: ["nombre", "hora"] }],
  },
};

// ── Correlación por NOMBRE DE PLANTILLA META (vista superadmin: parte de la Meta) ──
export const TURNOS_CORRELATION_BY_META = {
  turno_agendado:          { internalKey: "booking_confirmed",    event: "Confirmación de reserva",          varOrder: ["nombre", "servicio", "fecha", "hora"] },
  turno_reprogramado:      { internalKey: "appointment_rescheduled", event: "Turno reprogramado",            varOrder: ["nombre", "servicio", "fecha", "hora"] },
  recordatorio_turno:      { internalKey: "reminder",             event: "Recordatorio (con anticipación)",  varOrder: ["nombre", "servicio", "fecha", "hora"], oneToMany: true },
  recordatorio_hoy:        { internalKey: "reminder",             event: "Recordatorio (día del turno)",     varOrder: ["nombre", "hora", "servicio"],          oneToMany: true },
  confirmacion_asistencia: { internalKey: "confirmation_request", event: "Pedido de confirmación",           varOrder: ["nombre", "servicio", "fecha", "hora"] },
  es_tu_turno:             { internalKey: "llamar_siguiente",     event: "Pase / es tu turno",               varOrder: ["nombre", "servicio"] },
  consulta_llegada:        { internalKey: "arrival_check",        event: "Consulta de llegada",              varOrder: ["nombre", "hora"] },
  turno_liberado:          { internalKey: "rebalance_offer",      event: "Turno liberado (rebalanceo / lista de espera)", varOrder: ["nombre", "servicio", "fecha", "hora"], unwired: true },
};

// Los textos internos que NUNCA salen por plantilla Meta (siempre in-window/texto libre).
export const TURNOS_INTERNAL_WITHOUT_META = [
  "cancellation_confirmed",
  "waitinglist_added",
  "rebalance_offer",
  "waitinglist_offer",
];

// Línea de ayuda común a ambas vistas.
export const TURNOS_WINDOW_HELP =
  "Dentro de las 24 h de la última respuesta del cliente se envía el texto interno; " +
  "fuera de la ventana, la plantilla Meta. TurnosIA elige automáticamente.";
